import { Controller } from "@hotwired/stimulus";

// Shared file dropzone: click-to-browse, keyboard, drag-and-drop, with a
// chosen-file chip and a Remove button. Dropped files are assigned to the
// real input so plain form submits carry them. Hosts subscribe to the
// bubbled events dropzone:changed ({ detail: { file } }) and
// dropzone:cleared, and can reset programmatically via a Stimulus outlet
// (this.dropzoneOutlet.clear()).
// Connects to data-controller="dropzone" — rendered by shared/_dropzone.
export default class extends Controller {
  static targets = ["input", "prompt", "fileInfo", "filename", "filesize"];
  static values = { accept: { type: String, default: "" } };

  browse() {
    this.inputTarget.click();
  }

  dragOver(event) {
    event.preventDefault();
    this.element.classList.add(
      "border-primary-500",
      "bg-primary-50/50",
      "dark:bg-primary-900/10",
    );
  }

  dragLeave(event) {
    event.preventDefault();
    this.resetStyle();
  }

  drop(event) {
    event.preventDefault();
    this.resetStyle();
    const file = event.dataTransfer.files[0];
    if (!file || !this.accepts(file)) return;

    const transfer = new DataTransfer();
    transfer.items.add(file);
    this.inputTarget.files = transfer.files;
    this.fileChosen();
  }

  fileChosen() {
    const file = this.inputTarget.files[0];
    if (!file) return;

    this.filenameTarget.textContent = file.name;
    this.filesizeTarget.textContent = this.humanSize(file.size);
    this.promptTarget.classList.add("hidden");
    this.fileInfoTarget.classList.remove("hidden");
    this.dispatch("changed", { detail: { file } });
  }

  clear(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.inputTarget.value = "";
    this.promptTarget.classList.remove("hidden");
    this.fileInfoTarget.classList.add("hidden");
    this.dispatch("cleared");
  }

  // The native input enforces `accept` for browsed files; mirror it for drops.
  accepts(file) {
    if (this.acceptValue === "") return true;
    const name = file.name.toLowerCase();
    return this.acceptValue
      .split(",")
      .some((ext) => name.endsWith(ext.trim().toLowerCase()));
  }

  resetStyle() {
    this.element.classList.remove(
      "border-primary-500",
      "bg-primary-50/50",
      "dark:bg-primary-900/10",
    );
  }

  humanSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
